'use client';
/**
 * A7 — LA REVUE DES TRANCHES PÉDIATRIQUES.
 *
 * ── Le problème qu'il règle ────────────────────────────────────────────────
 *
 * Cinquante-trois lignes pédiatriques existent au registre, sur trente
 * paramètres — et quatre seulement sont adoptées. Les quarante-neuf autres sont
 * saisies, vivantes, et invisibles du public : un nourrisson lit aujourd'hui la
 * borne de l'adulte.
 *
 * Les adopter depuis le panneau du paramètre demanderait d'ouvrir trente
 * fiches, et de tenir de tête à chaque fois ce que l'adulte affiche pour juger
 * si l'écart est vraisemblable. Cet écran met les trois choses côte à côte : la
 * ligne pédiatrique, sa SOURCE, et la valeur ADULTE en regard.
 *
 * ⚠️ IL N'ADOPTE RIEN. L'adoption reste le geste signé qu'elle est, au panneau
 * du paramètre. Une revue qui adopterait en passant ferait publier des bornes
 * cliniques d'un seul clic de liste — et c'est précisément ce qu'on ne veut
 * pas d'un écran qui en montre cinquante-trois d'affilée.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { card, EmptyState, Skeleton } from '@/components/ui';
import { trancheAgeLisible } from '@/shared/ageLisible';
import { moteur } from './Registres';
import { useGed } from '@/lib/store';

import { Bouton } from '@/shared/Bouton';
import { fmtDate } from '@/lib/meta';
type Ligne = {
  documentId: string;
  sexe: string; grossesse: string;
  ageMin: number | null; ageMax: number | null;
  condition: string | null; nature: string;
  borneMin: number | null; borneMax: number | null;
  unite: string | null; texteLibre: string | null;
  source: string | null; commentaire: string | null;
  adopte: boolean; adopteLe: string | null;
};
type Parametre = {
  code: string; nom: string; unite: string | null;
  pediatriques: Ligne[]; adultes: Ligne[]; publieAujourdhui: Ligne[];
  nbPediatriques: number; nbPediatriquesAdoptees: number;
};

const nb = (v: number) => String(v).replace('.', ',');

/** La borne, dite comme la fiche publique la dit. */
function borne(l: Ligne): string {
  if (l.texteLibre) return l.texteLibre;
  const u = l.unite ? ` ${l.unite}` : '';
  if (l.borneMin !== null && l.borneMax !== null) return `${nb(l.borneMin)} – ${nb(l.borneMax)}${u}`;
  if (l.borneMax !== null) return `< ${nb(l.borneMax)}${u}`;
  if (l.borneMin !== null) return `> ${nb(l.borneMin)}${u}`;
  return '—';
}

/** La strate, sans répéter l'âge quand la colonne le porte déjà. */
function qui(l: Ligne): string {
  return [
    l.sexe === 'masculin' ? 'Homme' : l.sexe === 'feminin' ? 'Femme' : '',
    l.grossesse && l.grossesse !== 'indifferent' ? l.grossesse : '',
    l.condition || '',
  ].filter(Boolean).join(', ');
}

export function OngletPediatrie() {
  const [d, setD] = useState<{ parametres: Parametre[]; compte: Record<string, number>; rappel: string } | null>(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [aAdopterSeulement, setAAdopterSeulement] = useState(true);
  const ouvrirPanneau = useGed((s) => s.ouvrirPanneau);

  const charger = useCallback(() => {
    void moteur('manuel-ref/pediatrie').then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      setD(r.data as never);
    }).catch(() => setErreur('Le serveur n’a pas répondu.'));
  }, []);
  useEffect(charger, [charger]);

  if (erreur) return <EmptyState titre="Lecture impossible" indice={erreur} />;
  if (!d) return <Skeleton rows={4} />;

  const liste = aAdopterSeulement
    ? d.parametres.filter((p) => p.nbPediatriquesAdoptees < p.nbPediatriques)
    : d.parametres;

  if (!d.parametres.length) {
    return (
      <EmptyState titre="Aucune tranche pédiatrique au registre"
        indice="Une tranche est pédiatrique quand sa borne HAUTE d’âge est sous 18 ans." />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '88ch' }}>
        {d.rappel}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
        <span>
          <strong>{d.compte.lignes}</strong> ligne{d.compte.lignes > 1 ? 's' : ''} pédiatrique
          {d.compte.lignes > 1 ? 's' : ''} sur <strong>{d.compte.parametres}</strong> paramètre
          {d.compte.parametres > 1 ? 's' : ''}
        </span>
        {/* Le chiffre qui compte : ce qui n'est PAS publié. Un enfant y lit la
            borne de l'adulte, et rien à l'écran ne le lui dit. */}
        <span style={{
          fontWeight: 700,
          color: d.compte.aAdopter ? 'var(--status-warning)' : 'var(--green-700)',
        }}>
          {d.compte.aAdopter} non adoptée{d.compte.aAdopter > 1 ? 's' : ''}
        </span>
        <span style={{ flex: 1 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={aAdopterSeulement}
            onChange={(e) => setAAdopterSeulement(e.currentTarget.checked)} />
          Ne montrer que ce qui reste à adopter
        </label>
      </div>

      {liste.map((p) => {
        const deplie = ouvert === p.code;
        const reste = p.nbPediatriques - p.nbPediatriquesAdoptees;
        return (
          <div key={p.code} style={{ ...card, padding: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <button type="button" className="ged-btn"
                title={`Ouvrir le paramètre ${p.code} sans quitter cette revue`}
                onClick={() => ouvrirPanneau({ type: 'parametre', valeur: p.code, origine: 'Revue pédiatrique' })}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700,
                  color: 'var(--color-primary-active)',
                }}>{p.code}</button>
              <strong style={{ fontSize: 13.5 }}>{p.nom}</strong>
              <span style={{
                fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                background: reste ? 'var(--amber-50)' : 'var(--green-50)',
                color: reste ? 'var(--status-warning)' : 'var(--green-700)',
              }}>
                {reste ? `${reste} à adopter` : 'toutes adoptées'}
              </span>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setOuvert(deplie ? null : p.code)}>
                {deplie ? 'Replier' : 'Comparer'}
              </Bouton>
            </div>

            {deplie && (
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                {/* ── CE QUE LE PUBLIC LIT AUJOURD'HUI ────────────────────
                    En tête, parce que c'est l'état des lieux : sans lui, on
                    adopte sans savoir ce qu'on remplace. */}
                <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 11px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 }}>
                    Ce qu’un enfant lit aujourd’hui
                  </div>
                  {p.publieAujourdhui.length ? (
                    p.publieAujourdhui.map((l) => (
                      <div key={l.documentId} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                        {qui(l) && <span style={{ color: 'var(--text-muted)' }}>{qui(l)} · </span>}
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{borne(l)}</span>
                        <span style={{ color: 'var(--text-muted)' }}> — la valeur adulte</span>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Aucune valeur adulte adoptée non plus : la fiche publique n’affiche rien pour ce paramètre.
                    </div>
                  )}
                </div>

                {/* ── LES TRANCHES PÉDIATRIQUES ──────────────────────────── */}
                <div style={{ display: 'grid', gap: 6 }}>
                  {p.pediatriques.map((l) => (
                    <div key={l.documentId} style={{
                      border: `1px solid ${l.adopte ? 'var(--green-200)' : 'var(--border-default)'}`,
                      borderRadius: 'var(--radius-md)', padding: '8px 10px',
                    }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
                        <span style={{ minWidth: 128, color: 'var(--text-muted)' }}>
                          {trancheAgeLisible(l.ageMin, l.ageMax) || 'tous âges'}
                        </span>
                        {qui(l) && <span style={{ color: 'var(--text-muted)' }}>{qui(l)}</span>}
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{borne(l)}</span>
                        <span style={{ flex: 1 }} />
                        <span style={{
                          fontSize: 12.5, fontWeight: 700,
                          color: l.adopte ? 'var(--green-700)' : 'var(--status-warning)',
                        }}>
                          {l.adopte ? `adoptée le ${fmtDate(l.adopteLe || '')}` : 'non adoptée — invisible du public'}
                        </span>
                      </div>
                      {/* ⚠️ LA SOURCE EST LE CŒUR DE LA REVUE. C'est la première
                          question d'un auditeur, et c'est ce qui permet de juger
                          si la borne se défend. Elle n'est jamais masquée, même
                          longue. */}
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                        {l.source || <em>aucune source citée — une borne sans source ne se défend pas en audit</em>}
                      </div>
                      {l.commentaire && (
                        <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 3, lineHeight: 1.5 }}>
                          {l.commentaire}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  L’adoption se fait au panneau du paramètre — cliquez son code ci-dessus.
                  Cet écran compare, il ne publie pas.
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!liste.length && (
        <EmptyState titre="Toutes les tranches pédiatriques sont adoptées"
          indice="Décochez le filtre pour les revoir toutes." />
      )}
    </div>
  );
}
